import React, { useMemo, useState } from "react";
import styled from "styled-components";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import { Input, ControlLabel, Label } from "components/bootstrap";
import { Select, ConfirmDialog } from "components/common";
import { useGetPriorities, useGetStatuses, useGetInvestigations } from "security-app/hooks/useInvestigationsAPI";
import { getValueFromInput } from "util/FormsUtils";
import { defaultCompare } from "logic/DefaultCompare";
import { useUsersAndTeamsOptions } from "../hooks/useUsersAndTeamsOptions";
const DefaultLabel = styled(Label)`
  display: inline-flex;
  margin-left: 5px;
  vertical-align: inherit;
`;
const SelectWrapper = styled.div`
  margin-bottom: 1rem;
`;
function CreateInvestigationNotificationForm({ config, validation, onChange }) {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const { investigations } = useGetInvestigations({
    direction: null,
    filters: {},
    orderBy: null,
    page: 1,
    query: null,
    perPage: 1e3
  });
  const investigationOptions = useMemo(
    () => investigations.map((investigation) => ({
      value: investigation.id,
      label: investigation.name
    })).sort((a, b) => defaultCompare(a.label, b.label)),
    [investigations]
  );
  const { priorities, loadingPriorities } = useGetPriorities();
  const priorityOptions = useMemo(
    () => priorities.map((priority) => ({
      value: priority.priority,
      label: priority.text,
      default: priority.default
    })).sort((a, b) => defaultCompare(b.value, a.value)),
    [priorities]
  );
  const { statuses, loadingStatuses } = useGetStatuses();
  const statusOptions = useMemo(
    () => statuses.map((status) => ({ value: status.status, label: status.status, default: status.default })).sort((a, b) => defaultCompare(a.value, b.value)),
    [statuses]
  );
  const assignToOptions = useUsersAndTeamsOptions();
  const renderOption = (option) => /* @__PURE__ */ React.createElement("span", { key: option.value, title: option.value }, option.label, option.default && /* @__PURE__ */ React.createElement(DefaultLabel, { bsStyle: "primary", bsSize: "xsmall" }, "Default"));
  const propagateChange = (key, value) => {
    const nextConfig = cloneDeep(config);
    nextConfig[key] = value;
    if (key === "create_new_investigation" && value === true) nextConfig.investigation_id = null;
    onChange(nextConfig);
  };
  const handleChange = (event) => {
    const { name } = event.target;
    propagateChange(name, getValueFromInput(event.target));
    if (name === "create_new_investigation" && getValueFromInput(event.target) === true)
      setShowConfirmationDialog(true);
  };
  const cancelCreateNewInvestigationForAll = () => {
    propagateChange("create_new_investigation", false);
    setShowConfirmationDialog(false);
  };
  const confirmCreateNewInvestigationForAll = () => {
    propagateChange("create_new_investigation", true);
    setShowConfirmationDialog(false);
  };
  return /* @__PURE__ */ React.createElement("div", null, !loadingPriorities && !loadingStatuses && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "notification-investigation-create-new",
      name: "create_new_investigation",
      label: "Create a New Investigation for Every Alert",
      type: "checkbox",
      bsStyle: validation.errors.create_new_investigation ? "error" : null,
      help: get(
        validation,
        "errors.create_new_investigation[0]",
        "Create a new investigation any time an alert is triggered"
      ),
      checked: !!config.create_new_investigation,
      onChange: handleChange
    }
  ), !config.create_new_investigation && /* @__PURE__ */ React.createElement(SelectWrapper, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Investigation"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "notification-investigation-id",
      inputProps: { name: "investigation_id" },
      placeholder: "Select an investigation...",
      options: investigationOptions,
      value: config.investigation_id,
      onChange: (value) => handleChange({ target: { name: "investigation_id", value } }),
      optionRenderer: (option) => renderOption(option)
    }
  )), !config.investigation_id && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SelectWrapper, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Assign Investigation To"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "notification-investigation-assigned-to",
      inputProps: { name: "investigation_assigned_to" },
      placeholder: "Assign investigation to...",
      options: assignToOptions,
      value: config.investigation_assigned_to,
      onChange: (value) => handleChange({
        target: {
          name: "investigation_assigned_to",
          value
        }
      }),
      optionRenderer: (option) => renderOption(option)
    }
  )), /* @__PURE__ */ React.createElement(SelectWrapper, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Investigation Priority"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "notification-investigation-investigation-priority",
      inputProps: { name: "investigation_priority" },
      placeholder: "Select a priority...",
      options: priorityOptions,
      value: config.investigation_priority,
      onChange: (value) => handleChange({
        target: {
          name: "investigation_priority",
          value: +value
        }
      }),
      optionRenderer: (option) => renderOption(option)
    }
  )), /* @__PURE__ */ React.createElement(SelectWrapper, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Investigation Status"), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "notification-investigation-investigation-status",
      inputProps: { name: "investigation_status" },
      placeholder: "Select a status...",
      options: statusOptions,
      value: config.investigation_status,
      onChange: (value) => handleChange({ target: { name: "investigation_status", value } }),
      optionRenderer: (option) => renderOption(option)
    }
  ))), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmationDialog,
      hideCancelButton: true,
      title: "Are you sure you want to enable this?",
      onCancel: cancelCreateNewInvestigationForAll,
      onConfirm: confirmCreateNewInvestigationForAll
    },
    /* @__PURE__ */ React.createElement("div", null, "Enabling the creation of a new investigation for every alert triggered may result in a large number of investigations created. Are you sure this is the desired outcome you want?")
  )));
}
export default CreateInvestigationNotificationForm;
